/**
 * NeuralAvatar — renders Wav2Lip-generated video frames from backend WebSocket.
 *
 * Architecture
 * ─────────────
 * 1. Opens a binary WebSocket to /ws/neural-avatar when `active` is true.
 * 2. Backend streams JPEG binary frames at ~25 fps.
 * 3. Each frame is decoded off-thread via createImageBitmap() and pushed into
 *    a jitter buffer (up to JITTER_BUF_MAX frames / ~400 ms of headroom).
 * 4. An rAF loop drains the buffer at TARGET_FPS; on underrun it holds the
 *    last rendered frame so there is never a black flash.
 *
 * Audio / text piping (imperative handle)
 * ─────────────────────────────────────────
 *   sendPcm(buffer)  — Float32LE PCM at 16 kHz → backend runs Wav2Lip
 *   sendText(text)   — AI speech text → backend runs edge-tts → Wav2Lip
 * Call these from the parent via a ref:
 *   const avatarRef = useRef<NeuralAvatarHandle>(null);
 *   avatarRef.current?.sendText("Hello, how can I help you?");
 *
 * Backend setup (one-time, manual)
 * ─────────────────────────────────
 *   1. git clone https://github.com/Rudrabha/Wav2Lip backend/Wav2Lip
 *   2. Download wav2lip.pth → backend/checkpoints/wav2lip.pth
 *   3. Place idle face video  → backend/assets/base_avatar.mp4
 *   4. pip install edge-tts librosa soundfile
 * Without these, the backend streams the idle video (or a black frame).
 */

import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { Box, Typography } from '@mui/material';
import { API_BASE_URL } from '../../config/api';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface NeuralAvatarHandle {
  /** Send raw Float32LE PCM audio (16 kHz mono) to backend Wav2Lip. */
  sendPcm:  (pcm: ArrayBuffer) => void;
  /** Send AI speech text to backend; backend runs TTS → Wav2Lip. */
  sendText: (text: string) => void;
  /** Returns the Web Audio MediaStream carrying AI speech output (for recording). */
  getAudioStream: () => MediaStream | null;
}

export interface NeuralAvatarProps {
  /** Open the WebSocket and start streaming when true. */
  active:       boolean;
  width:        number;
  height:       number;
  /** Forwarded to backend as a hint; controls idle-loop pacing. */
  agentTalking?: boolean;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const API_BASE = API_BASE_URL;
const WS_URL = API_BASE.replace(/^http/, 'ws') + '/ws/neural-avatar';

const TARGET_FPS     = 25;
const FRAME_MS       = 1000 / TARGET_FPS;   // ~40 ms
const JITTER_BUF_MAX = 10;                  // frames  → ~400 ms of headroom
const PING_INTERVAL  = 20_000;              // keepalive ping every 20 s

// ─── Component ────────────────────────────────────────────────────────────────

export const NeuralAvatar = forwardRef<NeuralAvatarHandle, NeuralAvatarProps>(
  ({ active, width, height, agentTalking = false }, ref) => {

    const canvasRef   = useRef<HTMLCanvasElement>(null);
    const wsRef       = useRef<WebSocket | null>(null);
    const bufRef      = useRef<ImageBitmap[]>([]);       // jitter buffer
    const lastBmRef   = useRef<ImageBitmap | null>(null); // hold-frame on underrun
    const rafRef      = useRef<number>(0);
    const prevTickRef = useRef<number>(0);
    const pingRef     = useRef<ReturnType<typeof setInterval> | null>(null);

    // Web Audio — plays TTS audio frames back to the user and exposes the stream
    // for recording.  Created once when active=true; destroyed on deactivation.
    const audioCtxRef  = useRef<AudioContext | null>(null);
    const audioDestRef = useRef<MediaStreamAudioDestinationNode | null>(null);

    const [status, setStatus] = useState<'idle' | 'connecting' | 'ready' | 'error'>('idle');

    // ── Imperative handle: parent can push PCM or text ────────────────────────
    useImperativeHandle(ref, () => ({
      sendPcm(pcm: ArrayBuffer) {
        if (wsRef.current?.readyState === WebSocket.OPEN) {
          wsRef.current.send(pcm);
        }
      },
      sendText(text: string) {
        if (wsRef.current?.readyState === WebSocket.OPEN) {
          wsRef.current.send(JSON.stringify({ type: 'tts_text', text }));
        }
      },
      getAudioStream(): MediaStream | null {
        return audioDestRef.current?.stream ?? null;
      },
    }));

    // ── Notify backend when talking state changes ──────────────────────────────
    useEffect(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: 'talking', active: agentTalking }));
      }
    }, [agentTalking]);

    // ── rAF render loop ───────────────────────────────────────────────────────
    const startRenderLoop = () => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      const tick = (now: number) => {
        rafRef.current = requestAnimationFrame(tick);

        // Throttle to TARGET_FPS — skip frames that arrive too early
        if (now - prevTickRef.current < FRAME_MS - 2) return;
        prevTickRef.current = now;

        const bm = bufRef.current.shift();
        if (bm) {
          ctx.drawImage(bm, 0, 0, canvas.width, canvas.height);
          // Close previous hold-frame before replacing
          lastBmRef.current?.close();
          lastBmRef.current = bm;
        } else if (lastBmRef.current) {
          // Underrun — hold last frame rather than going black
          ctx.drawImage(lastBmRef.current, 0, 0, canvas.width, canvas.height);
        } else {
          ctx.fillStyle = '#0a0a18';
          ctx.fillRect(0, 0, canvas.width, canvas.height);
        }
      };

      rafRef.current = requestAnimationFrame(tick);
    };

    const stopRenderLoop = () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = 0;
      }
    };

    const flushBuffer = () => {
      bufRef.current.forEach(bm => bm.close());
      bufRef.current = [];
      lastBmRef.current?.close();
      lastBmRef.current = null;
    };

    // ── Audio frame playback ──────────────────────────────────────────────────
    const playAudioFrame = async (wav: ArrayBuffer) => {
      const ctx = audioCtxRef.current;
      const dest = audioDestRef.current;
      if (!ctx || !dest) return;
      try {
        const decoded = await ctx.decodeAudioData(wav.slice(0));
        const src = ctx.createBufferSource();
        src.buffer = decoded;
        src.connect(ctx.destination); // speakers
        src.connect(dest);            // recording destination
        src.start();
      } catch {
        // Malformed WAV frame — ignore
      }
    };

    // ── WebSocket lifecycle ───────────────────────────────────────────────────
    useEffect(() => {
      if (!active) {
        setStatus('idle');
        stopRenderLoop();
        flushBuffer();
        if (pingRef.current) { clearInterval(pingRef.current); pingRef.current = null; }
        wsRef.current?.close();
        wsRef.current = null;
        // Tear down audio context
        audioCtxRef.current?.close().catch(() => {});
        audioCtxRef.current = null;
        audioDestRef.current = null;
        return;
      }

      // Create a fresh AudioContext + recording destination
      const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
      const ctx: AudioContext = new AudioCtx();
      audioCtxRef.current  = ctx;
      audioDestRef.current = ctx.createMediaStreamDestination();

      setStatus('connecting');
      const ws = new WebSocket(WS_URL);
      ws.binaryType = 'arraybuffer';
      wsRef.current = ws;

      ws.onopen = () => {
        setStatus('ready');
        startRenderLoop();
        // Keepalive pings so the server doesn't time out on silent periods
        pingRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: 'ping' }));
          }
        }, PING_INTERVAL);
      };

      ws.onmessage = async (ev: MessageEvent) => {
        if (!(ev.data instanceof ArrayBuffer)) return;
        const buf = ev.data;

        // Audio frame: magic prefix 0xFE 0xED followed by WAV bytes
        const header = new Uint8Array(buf, 0, 2);
        if (header[0] === 0xFE && header[1] === 0xED) {
          await playAudioFrame(buf.slice(2));
          return;
        }

        // JPEG frame
        if (buf.byteLength > 100) {
          if (bufRef.current.length >= JITTER_BUF_MAX) {
            bufRef.current.shift()?.close();
          }
          try {
            const blob = new Blob([buf], { type: 'image/jpeg' });
            const bm   = await createImageBitmap(blob);
            bufRef.current.push(bm);
          } catch {
            // Malformed JPEG — ignore
          }
        }
        // Text control messages (status, pong, error) — no UI action needed
      };

      ws.onerror = () => setStatus('error');

      ws.onclose = () => {
        if (pingRef.current) { clearInterval(pingRef.current); pingRef.current = null; }
        if (wsRef.current === ws) {
          setStatus('idle');
          stopRenderLoop();
          wsRef.current = null;
        }
      };

      return () => {
        if (pingRef.current) { clearInterval(pingRef.current); pingRef.current = null; }
        ws.close();
        wsRef.current = null;
        stopRenderLoop();
        flushBuffer();
        ctx.close().catch(() => {});
        audioCtxRef.current = null;
        audioDestRef.current = null;
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [active]);

    // ─────────────────────────────────────────────────────────────────────────

    return (
      <Box
        sx={{
          position: 'relative',
          width,
          height,
          bgcolor: '#0a0a18',
          borderRadius: 1,
          overflow: 'hidden',
          flexShrink: 0,
        }}
      >
        <canvas
          ref={canvasRef}
          width={width}
          height={height}
          style={{ display: 'block', width: '100%', height: '100%' }}
        />

        {/* Overlay shown while not streaming */}
        {status !== 'ready' && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
            }}
          >
            <Box
              sx={{
                width: 52,
                height: 52,
                borderRadius: '50%',
                bgcolor: 'rgba(31,147,255,0.1)',
                border: '2px solid rgba(31,147,255,0.25)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                animation:
                  status === 'connecting'
                    ? 'hub-pulse 1.4s ease-out infinite'
                    : 'none',
              }}
            >
              <Box component="span" sx={{ fontSize: 24, lineHeight: 1 }}>
                🤖
              </Box>
            </Box>
            <Typography
              variant="caption"
              sx={{
                color: status === 'error' ? 'error.main' : 'grey.600',
                fontSize: '0.6rem',
                letterSpacing: '0.1em',
                fontWeight: 600,
              }}
            >
              {status === 'connecting'
                ? 'CONNECTING…'
                : status === 'error'
                ? 'UNAVAILABLE'
                : 'STANDBY'}
            </Typography>
          </Box>
        )}
      </Box>
    );
  },
);

NeuralAvatar.displayName = 'NeuralAvatar';
